import { supabase } from './supabaseClient'
import type {
  Resume,
  ResumeRow,
  ResumeTemplate,
  ResumeContact,
  ResumeExperience,
  ResumeEducation,
  ResumeSkillGroup,
  ResumeProject,
  ResumeCertification,
  ResumeLanguage,
  ResumeCustomSection,
} from '../types'

const TABLE = 'resumes'

function rowToResume(row: ResumeRow): Resume {
  return {
    id: row.id,
    userId: row.user_id,
    title: row.title,
    template: row.template,
    isMaster: row.is_master,
    tailoredForApplicationId: row.tailored_for_application_id,
    contact: (row.contact ?? {}) as ResumeContact,
    summary: row.summary,
    experience: (row.experience ?? []) as ResumeExperience[],
    education: (row.education ?? []) as ResumeEducation[],
    skills: (row.skills ?? []) as ResumeSkillGroup[],
    projects: (row.projects ?? []) as ResumeProject[],
    certifications: (row.certifications ?? []) as ResumeCertification[],
    languages: (row.languages ?? []) as ResumeLanguage[],
    customSections: (row.custom_sections ?? []) as ResumeCustomSection[],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function resumeToInsert(input: Partial<Resume> & { userId: string }) {
  return {
    user_id: input.userId,
    title: input.title ?? 'Untitled resume',
    template: input.template ?? 'modern',
    is_master: input.isMaster ?? false,
    tailored_for_application_id: input.tailoredForApplicationId ?? null,
    contact: input.contact ?? {},
    summary: input.summary ?? null,
    experience: input.experience ?? [],
    education: input.education ?? [],
    skills: input.skills ?? [],
    projects: input.projects ?? [],
    certifications: input.certifications ?? [],
    languages: input.languages ?? [],
    custom_sections: input.customSections ?? [],
  }
}

function resumeToUpdate(patch: Partial<Resume>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (patch.title !== undefined) out.title = patch.title
  if (patch.template !== undefined) out.template = patch.template
  if (patch.isMaster !== undefined) out.is_master = patch.isMaster
  if (patch.tailoredForApplicationId !== undefined) {
    out.tailored_for_application_id = patch.tailoredForApplicationId
  }
  if (patch.contact !== undefined) out.contact = patch.contact
  if (patch.summary !== undefined) out.summary = patch.summary
  if (patch.experience !== undefined) out.experience = patch.experience
  if (patch.education !== undefined) out.education = patch.education
  if (patch.skills !== undefined) out.skills = patch.skills
  if (patch.projects !== undefined) out.projects = patch.projects
  if (patch.certifications !== undefined) out.certifications = patch.certifications
  if (patch.languages !== undefined) out.languages = patch.languages
  if (patch.customSections !== undefined) out.custom_sections = patch.customSections
  return out
}

export async function listResumes(userId: string): Promise<Resume[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
  if (error) throw new Error(`Failed to load resumes: ${error.message}`)
  return (data ?? []).map(rowToResume)
}

export async function getResume(id: string, userId: string): Promise<Resume | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single()
  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(`Failed to load resume: ${error.message}`)
  }
  return rowToResume(data)
}

export async function createResume(input: Partial<Resume> & { userId: string }): Promise<Resume> {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(resumeToInsert(input))
    .select()
    .single()
  if (error) throw new Error(`Failed to create resume: ${error.message}`)
  return rowToResume(data)
}

export async function updateResume(id: string, userId: string, patch: Partial<Resume>): Promise<Resume> {
  const { data, error } = await supabase
    .from(TABLE)
    .update(resumeToUpdate(patch))
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single()
  if (error) throw new Error(`Failed to update resume: ${error.message}`)
  return rowToResume(data)
}

export async function deleteResume(id: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', userId)
  if (error) throw new Error(`Failed to delete resume: ${error.message}`)
}

export async function duplicateResume(id: string, userId: string, overrides?: Partial<Resume>): Promise<Resume> {
  const source = await getResume(id, userId)
  if (!source) throw new Error('Resume not found')
  return createResume({
    userId,
    title: overrides?.title ?? `${source.title} (copy)`,
    template: overrides?.template ?? source.template,
    isMaster: false,
    tailoredForApplicationId: overrides?.tailoredForApplicationId ?? null,
    contact: source.contact,
    summary: source.summary,
    experience: source.experience,
    education: source.education,
    skills: source.skills,
    projects: source.projects,
    certifications: source.certifications,
    languages: source.languages,
    customSections: source.customSections,
  })
}

/**
 * Mark a resume as master. Clears the master flag on any other resume first
 * (partial unique index would otherwise reject the update).
 */
export async function setMaster(id: string, userId: string): Promise<void> {
  const { error: clearErr } = await supabase
    .from(TABLE)
    .update({ is_master: false })
    .eq('user_id', userId)
    .eq('is_master', true)
    .neq('id', id)
  if (clearErr) throw new Error(`Failed to clear existing master: ${clearErr.message}`)

  const { error } = await supabase
    .from(TABLE)
    .update({ is_master: true })
    .eq('id', id)
    .eq('user_id', userId)
  if (error) throw new Error(`Failed to set master: ${error.message}`)
}

export async function fetchMasterResume(userId: string): Promise<Resume | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('is_master', true)
    .limit(1)
    .maybeSingle()
  if (error) throw new Error(`Failed to load master resume: ${error.message}`)
  return data ? rowToResume(data) : null
}

/**
 * Call the tailor-resume Edge Function to create a new tailored resume.
 * Returns the new resume id.
 */
export async function tailorResumeForApplication(
  sourceResumeId: string,
  applicationId: string
): Promise<{ resumeId: string }> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('No active session. Please log in again.')

  const { data, error } = await supabase.functions.invoke<{ resumeId: string; error?: string }>(
    'tailor-resume',
    {
      body: { sourceResumeId, applicationId },
      headers: { Authorization: `Bearer ${session.access_token}` },
    }
  )
  if (error) throw new Error(`Tailor failed: ${error.message}`)
  if (!data) throw new Error('No data returned from tailor-resume')
  if ('error' in data && data.error) throw new Error(data.error)
  return { resumeId: data.resumeId }
}

/**
 * Call the parse-resume Edge Function to create a new resume by importing
 * the user's most-recently-analyzed uploaded resume.
 * Returns the new resume id.
 */
export async function importResumeFromAnalysis(
  resumeAnalysisId: string
): Promise<{ resumeId: string }> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('No active session. Please log in again.')

  const { data, error } = await supabase.functions.invoke<{ resumeId: string; error?: string }>(
    'parse-resume',
    {
      body: { resumeAnalysisId },
      headers: { Authorization: `Bearer ${session.access_token}` },
    }
  )
  if (error) throw new Error(`Import failed: ${error.message}`)
  if (!data) throw new Error('No data returned from parse-resume')
  if ('error' in data && data.error) throw new Error(data.error)
  return { resumeId: data.resumeId }
}

// Re-export type helper for places that need a default
export const DEFAULT_TEMPLATE: ResumeTemplate = 'modern'
